import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FieldArray, getIn } from 'formik';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Typography,
    FormHelperText,
    Box,
    Switch,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Input,
    CircularProgress,
    FormControlLabel,
    InputAdornment
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { FeeScheduleSelection } from '../../../feeSchedulePage/feeScheduleSelectionComponent';
import { checkRequiresGlobalConfig } from '../../../../store/api/globalFeeSchedule';
import { getFeescheduleRateLabel } from '../../../../store/api/feeScheduleItems';
import GlobalFeeScheduleFields from '../GlobalFeeScheduleFields';
import RuleProviderRoleCriteriaFieldArray from './RuleProviderRoleCriteriaFieldArray';
import { PopoverLink } from '../../../../common';

// Simple date formatter
const formatDate = (dateString: string | Date, format?: string): string => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (format === 'YYYY-MM-DD') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Default format: M/D/YYYY
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

interface ProviderRolePaymentGroupEntry {
    payMethodId: number | null;
    payValue: number | null;
    // Local Fee Schedule fields
    feeScheduleId: number | null;
    feeScheduleName: string;
    feeScheduleLabelToRateXRefId: number | null;
    // Global Fee Schedule fields
    globalFeeScheduleId?: number | null;
    globalFeeScheduleLabelToRateXRefId?: number | null;
    globalFeeScheduleGPCI?: string | null;
    globalFeeScheduleState?: string | null;
    globalFeeScheduleLocality?: string | null;
    useGlobalFeeSchedules: boolean | false;
    feeSchedule?: any; // Store fee schedule object to access dates
    globalFeeSchedule?:any;
    // UI-only Global Fee Schedule Configuration fields
    requiresGlobalConfig?: boolean;
    selectedRateType?: string | null;
    selectedState?: string | null;
    selectedLocality?: string | null;
    selectedGpci?: string | null;
    global_schedule_rate_column?: string | null;
    dateRange?: any | null;
    rateLabels: any;
    isPerUnit: boolean;
    //Anesthesia
    useMinutesPerUnit?: boolean;
    minutesPerUnit?: number | null;
}

interface RuleProviderRolePaymentGroupArrayProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    setFieldError: (field: string, message: string | undefined) => void;
    errors: any;
    touched: any;
    handleChange: (e: any) => void;
    handleBlur: (e: any) => void;
    payMethods: any[];
    classes: any,
    getFeeScheduleLabels: (feeScheduleId: number, callback: any, labelId: any, setFieldValue: any, useGlobalFeeSchedule?: boolean) => void;
    getFeeSchedule: (feeScheduleId: number, callback: (feeSchedule: any) => void) => void;
    handleHoverFeeSchedule?: (element: HTMLElement, feeScheduleId?: number | null, feeScheduleData?: any, isGlobal?: boolean, regionalFilters?: { state?: string | null, gpci?: string | null, locality?: string | null } | null) => void;
    popoverLeave?: () => void;
    rateLabelValidationErrors: Record<number, boolean>;
    setRateLabelValidationErrors: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    providerRoleCriteria: any[];
    npiRoles: any[];
    areAllPayMethodsPerUnit: (values: any) => boolean;
    IsPaySourceTypeSchedule: (paySourceId: any) => boolean;
    isPayMethodFlatRate?:any
    isPayMethodAnesthesia: (payMethodId: any) => boolean;
    isPayMethodPercentage: (payMethodId: any) => boolean;
}

const RuleProviderRolePaymentGroupFieldArray: React.FC<RuleProviderRolePaymentGroupArrayProps> = ({
    values,
    setFieldValue,
    setFieldError,
    errors,
    touched,
    handleChange,
    handleBlur,
    payMethods,
    classes,
    getFeeScheduleLabels,
    getFeeSchedule,
    handleHoverFeeSchedule,
    popoverLeave,
    rateLabelValidationErrors,
    setRateLabelValidationErrors,
    providerRoleCriteria,
    npiRoles,
    areAllPayMethodsPerUnit,
    IsPaySourceTypeSchedule,
    isPayMethodFlatRate=undefined,
    isPayMethodAnesthesia,
    isPayMethodPercentage=undefined

}) => {
    const entries = values.providerRolePaymentGroupXref || [];
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingTypeChange, setPendingTypeChange] = useState<'local' | 'global' | null>(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [validatingRateLabel, setValidatingRateLabel] = useState<Record<number, boolean>>({});

    const getHelperText = (index, name) => {
        return (
            getIn(errors, `providerRolePaymentGroupXref.${index}.${name}`)
        );
    };

    const hasError = (index, name) => {
        return Boolean(
            getIn(touched, `providerRolePaymentGroupXref.${index}.${name}`) &&
            getIn(errors, `providerRolePaymentGroupXref.${index}.${name}`)
        );
    };

    const handleTypeChange = (fieldName, newType: 'local' | 'global') => {
        setSelectedGroup(fieldName);
        const groups = values.providerRolePaymentGroupXref || [];
        const hasData = groups.some((g: any) => g.feeScheduleId || g.globalFeeScheduleId);

        if (hasData) {
            setPendingTypeChange(newType);
            setShowConfirmDialog(true);
        } else {
            setFieldValue(`${fieldName}.useGlobalFeeSchedules`, newType === 'global');
        }
    };

    const handleConfirmTypeChange = () => {
        setFieldValue(`${selectedGroup}.useGlobalFeeSchedules`, pendingTypeChange === 'global');

        // Reset to one default group
        const defaultGroup = {
            id: null,
            payMethodId: null,
            payValue: '',
            feeScheduleId: null,
            feeScheduleName: '',
            feeScheduleLabelToRateXRefId: null,
            rateLabels: [],
            feeSchedule: null,
            useGlobalFeeSchedules: pendingTypeChange === 'global',
            globalFeeScheduleId: null,
            globalFeeScheduleLabelToRateXRefId: null,
            globalFeeSchedule: null,
            globalFeescheduleState: null,
            globalFeescheduleLocality: null,
            globalFeescheduleGPCI: null,
            // Global config fields
            requiresGlobalConfig: false,
            selectedRateType: null,
            selectedState: null,
            selectedLocality: null,
            selectedGpci: null,
            global_schedule_rate_column: null,
            dateRange: null,
            providerRolePaymentCriteriaXref: [{
                providerRoleSelectionCriteriaId: null,
                type: null,
                equator: '',
                value: ''
            }],
            isPerUnit: false,
        };

        setFieldValue(`${selectedGroup}`, defaultGroup);
        setShowConfirmDialog(false);
        setPendingTypeChange(null);
    };

    const handleFeeScheduleSelect = (groupIndex: number, rowData: any) => {
        const prefix = `providerRolePaymentGroupXref[${groupIndex}]`;

        const isGlobalSchedule = rowData.isGlobal || rowData.isRBRVS;
        const isRBRVSSchedule = Boolean(rowData.isRBRVS);
        const showGlobalConfig = isGlobalSchedule && isRBRVSSchedule;

        // Auto-set toggle based on selected fee schedule type
        if (isGlobalSchedule) {
            //setGroupFeeScheduleType('global');
            setFieldValue(`${prefix}.useGlobalFeeSchedules`, true);
        } else {
            //setGroupFeeScheduleType('local');
            setFieldValue(`${prefix}.useGlobalFeeSchedules`, false);
        }

        if (isGlobalSchedule) {
            setFieldValue(`${prefix}.globalFeeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.globalFeeSchedule`, rowData);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.rateLabels`, rowData.globalFeescheduleLabelToRateXRef || []);
            setFieldValue(`${prefix}.feeScheduleId`, null);
            setFieldValue(`${prefix}.feeSchedule`, null);
            setFieldValue(`${prefix}.feeScheduleLabelToRateXRefId`, null);
        } else {
            setFieldValue(`${prefix}.feeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.feeSchedule`, rowData);
            // Don't set empty rateLabels here - they will be fetched via getFeeScheduleLabels callback below
            setFieldValue(`${prefix}.globalFeeScheduleId`, null);
            setFieldValue(`${prefix}.globalFeeSchedule`, null);
            setFieldValue(`${prefix}.globalFeeScheduleLabelToRateXRefId`, null);
        }

        setFieldValue(`${prefix}.requiresGlobalConfig`, showGlobalConfig);

        if (showGlobalConfig) {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        } else {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.globalFeescheduleState`, null);
            setFieldValue(`${prefix}.globalFeescheduleLocality`, null);
            setFieldValue(`${prefix}.globalFeescheduleGPCI`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        }

        if (!isGlobalSchedule && getFeeSchedule) {
            getFeeSchedule(rowData.id, (feeSchedule: any) => {
                setFieldValue(`${prefix}.feeSchedule`, feeSchedule);
            });
        }

        if (getFeeScheduleLabels) {
            const rateLabelCallback = (newFeeScheduleLabels: any) => {
                setFieldValue(`${prefix}.rateLabels`, newFeeScheduleLabels);
            };
            getFeeScheduleLabels(rowData.id, rateLabelCallback, null, setFieldValue, isGlobalSchedule);
        }
    };

    const shouldShowPerUnitField = (values, entry) => {
        return ((IsPaySourceTypeSchedule(values.paySourceId) && isPayMethodPercentage(entry.payMethodId)) || (!IsPaySourceTypeSchedule(values.paySourceId) && isPayMethodFlatRate(entry.payMethodId)))
    }
    
    return (
        <>
        <FieldArray
            name="providerRolePaymentGroupXref"
            render={({ remove, push }) => (
                <Grid container spacing={2}>
                    {entries.map((entry: ProviderRolePaymentGroupEntry, index: number) => {
                        const fieldName = `providerRolePaymentGroupXref[${index}]`;
                        const fieldErrors = errors?.providerRolePaymentGroupXref?.[index];
                        const fieldTouched = touched?.providerRolePaymentGroupXref?.[index];

                        return (
                            <Grid item xs={12} key={index}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={10} display="flex" alignItems="center" sx={{ mt:2, mb: 2 }}>
                                        {IsPaySourceTypeSchedule(values.paySourceId) && 
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">Local Fee Schedules</Typography>
                                                <Switch
                                                    size="small"
                                                    checked={entry.useGlobalFeeSchedules}
                                                    onChange={(e) => handleTypeChange(fieldName, e.target.checked ? 'global' : 'local')}
                                                />
                                                <Typography variant="body2">Global Fee Schedules</Typography>
                                            </Box>
                                        }
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 1
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Group {index + 1}
                                                    </Typography>

                                                    {index === 0 && (
                                                        <Tooltip title="Add Group">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                disabled={!entry.payMethodId}
                                                                onClick={() =>
                                                                    push({
                                                                        payMethodId: null,
                                                                        payValue: null,
                                                                        feeScheduleId: null,
                                                                        feeScheduleName: '',
                                                                        feeScheduleLabelToRateXRefId: null,
                                                                        rateLabels: [],
                                                                        feeSchedule: null,
                                                                        useGlobalFeeSchedules: false,
                                                                        globalFeeScheduleId: null,
                                                                        globalFeeScheduleLabelToRateXRefId: null,
                                                                        globalFeeSchedule: null,
                                                                        globalFeescheduleState: null,
                                                                        globalFeescheduleLocality: null,
                                                                        globalFeescheduleGPCI: null,
                                                                        // Global config fields
                                                                        requiresGlobalConfig: false,
                                                                        selectedRateType: null,
                                                                        selectedState: null,
                                                                        selectedLocality: null,
                                                                        selectedGpci: null,
                                                                        global_schedule_rate_column: null,
                                                                        dateRange: null,
                                                                        providerRolePaymentCriteriaXref: [
                                                                            {
                                                                                providerRoleSelectionCriteriaId: null,
                                                                                type: null,
                                                                                equator: '',
                                                                                value: ''
                                                                            }
                                                                        ],
                                                                        isPerUnit: false,
                                                                    })
                                                                }
                                                            >
                                                                <AddCircleIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {entries.length > 1 && (
                                                        <Tooltip title="Delete Group">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => remove(index)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    {IsPaySourceTypeSchedule(values.paySourceId) && <>
                                    <Grid item xs={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel className="MuiInputLabel-shrink" style={{ fontSize: 11, marginTop: -30, marginLeft: 5 }}>
                                                Fee Schedule *
                                            </InputLabel>
                                            <Box
                                                // Only attach hover handlers for LOCAL schedules
                                                onMouseEnter={ (event) =>
                                                        handleHoverFeeSchedule && handleHoverFeeSchedule(
                                                            event.currentTarget,
                                                            entry?.feeScheduleId || entry?.globalFeeScheduleId,
                                                            entry?.feeSchedule || entry?.globalFeeSchedule,
                                                            entry?.useGlobalFeeSchedules,
                                                            entry?.globalFeeScheduleId ? { state: entry?.selectedState, gpci: entry?.selectedGpci, locality: entry?.selectedLocality } : null
                                                        )
                                                }
                                                onMouseLeave={entry.useGlobalFeeSchedules == false ? popoverLeave : undefined}
                                                onClick={entry.useGlobalFeeSchedules == false ? popoverLeave : undefined}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiChip-root': {
                                                        maxWidth: '100% !important'
                                                    },
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block'
                                                    }
                                                }}
                                                marginTop={1}
                                            >
                                                <FeeScheduleSelection
                                                    value={
                                                        (entry?.feeScheduleId || entry?.globalFeeScheduleId)
                                                            ? entry?.feeScheduleName
                                                            : ''
                                                    }
                                                    defaultValue='Search Fee Schedule'
                                                    feeScheduleType={entry.useGlobalFeeSchedules === true ? 'global' : 'local'}
                                                    hideTypeToggle={true}
                                                    handleSetFeeSchedule={(rowData: any) =>
                                                        handleFeeScheduleSelect(index, rowData)
                                                    }
                                                />
                                            </Box>
                                            <FormHelperText error>
                                                {getHelperText(index, "feeScheduleId") || ' '}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    {entry.useGlobalFeeSchedules === false && entry?.feeSchedule?.effectiveDate && entry?.feeSchedule?.expirationDate && (
                                        <Grid item xs={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date Range"
                                                value={`${formatDate(entry.feeSchedule.effectiveDate)} - ${formatDate(entry.feeSchedule.expirationDate)}`}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        textAlign: 'left',
                                                        color: 'text.secondary',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    {(entry?.feeScheduleId || entry?.globalFeeScheduleId) && (
                                        <Grid item xs={2}>
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={hasError(index, entry.useGlobalFeeSchedules === true
                                                    ? `${fieldName}.globalFeeScheduleLabelToRateXRefId`
                                                    : `${fieldName}.feeScheduleLabelToRateXRefId`)}
                                                sx={{ position: 'relative', marginTop: 2  }}
                                            >
                                                <InputLabel>Rate Label</InputLabel>
                                                <Select
                                                    size="small"
                                                    name={entry.useGlobalFeeSchedules === true
                                                        ? `${fieldName}.globalFeeScheduleLabelToRateXRefId`
                                                        : `${fieldName}.feeScheduleLabelToRateXRefId`}
                                                    value={(entry.useGlobalFeeSchedules === true
                                                        ? entry?.globalFeeScheduleLabelToRateXRefId
                                                        : entry?.feeScheduleLabelToRateXRefId) || ''}
                                                    onChange={async (e) => {
                                                        handleChange(e);
                                                        // Only validate for LOCAL fee schedules, NOT global
                                                        if (e.target.value && entry.useGlobalFeeSchedules === false) {
                                                            const feeScheduleId = entry.feeScheduleId;
                                                            const labelId = Number(e.target.value);
                                                            const rateLabelField = entry.useGlobalFeeSchedules ? `${fieldName}.globalFeeScheduleLabelToRateXRefId` : `${fieldName}.feeScheduleLabelToRateXRefId`;

                                                            // Set loading state
                                                            setValidatingRateLabel(prev => ({
                                                                ...prev,
                                                                [index]: true
                                                            }));

                                                            try {
                                                                // Call the API directly (not the Redux action)
                                                                const hasNoValue = await getFeescheduleRateLabel(
                                                                    feeScheduleId,
                                                                    labelId
                                                                );

                                                                // Update state to track validation errors
                                                                setRateLabelValidationErrors(prev => ({
                                                                    ...prev,
                                                                    [index]: hasNoValue
                                                                }));

                                                                if (hasNoValue) {
                                                                    // API returned true = rate has no value = error
                                                                    setFieldError(rateLabelField, 'This rate label has no value');
                                                                } else {
                                                                    // API returned false = rate has a value = clear error
                                                                    setFieldError(rateLabelField, undefined);
                                                                }
                                                            } catch (error) {
                                                                // Silently handle validation errors
                                                            } finally {
                                                                // Clear loading state
                                                                setValidatingRateLabel(prev => ({
                                                                    ...prev,
                                                                    [index]: false
                                                                }));
                                                            }
                                                        } else {
                                                            // Clear validation error for this group
                                                            setRateLabelValidationErrors(prev => ({
                                                                ...prev,
                                                                [index]: false
                                                            }));
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        handleBlur(e);
                                                        // Re-apply the error if validation state says there's an error
                                                        // This prevents Formik from clearing the error on blur
                                                        if (rateLabelValidationErrors[index]) {
                                                            setFieldError(entry.useGlobalFeeSchedules === true ? `${fieldName}.globalFeeScheduleLabelToRateXRefId` : `${fieldName}.feeScheduleLabelToRateXRefId`, 'This rate label has no value');
                                                        }
                                                    }}
                                                    label="Rate Label"
                                                >
                                                    <MenuItem value="">
                                                        <em>Select Rate Label</em>
                                                    </MenuItem>
                                                    {entry?.rateLabels?.map((label: any) => (
                                                        <MenuItem key={label.id} value={label.id}>
                                                            {label.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {validatingRateLabel[index] && (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 32,
                                                            top: '50%',
                                                            marginTop: '-10px',
                                                            zIndex: 1
                                                        }}
                                                    />
                                                )}
                                                <FormHelperText error>
                                                    {getHelperText(index, entry.useGlobalFeeSchedules === true ? `globalFeeScheduleLabelToRateXRefId` : `feeScheduleLabelToRateXRefId`) || ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    )}
                                    {(entry.useGlobalFeeSchedules === true && entry?.globalFeeScheduleId && (entry?.requiresGlobalConfig || entry?.feeScheduleName?.toLowerCase()=="dme")) && (
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={12}>
                                                <GlobalFeeScheduleFields
                                                    scheduleId={entry.globalFeeScheduleId}
                                                    isRBRVS={(entry.requiresGlobalConfig || entry?.feeScheduleName?.toLowerCase()=="dme")}
                                                    values={{
                                                        selectedRateType: entry.selectedRateType || null,
                                                        selectedState: entry.selectedState || null,
                                                        selectedLocality: entry.selectedLocality || null,
                                                        selectedGpci: entry.selectedGpci || null,
                                                        isDME:entry?.feeScheduleName?.toLowerCase()=="dme"
                                                    }}
                                                    onChange={(field, value) => {
                                                        setFieldValue(`${fieldName}.${field}`, value);
                                                    }}
                                                    errors={{
                                                        selectedRateType: getHelperText(index, "selectedRateType") || '',
                                                        selectedState: getHelperText(index, "selectedState") || '',
                                                        selectedLocality: getHelperText(index, "selectedLocality") || '',
                                                        selectedGpci: getHelperText(index, "selectedGpci") || ''
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                    </>
                                    }
                                    <Grid item xs={3}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor={`${fieldName}.payMethodId`}>
                                                Pay Method
                                            </InputLabel>
                                            <Select
                                                required
                                                value={entry.payMethodId}
                                                onChange={(e) => {
                                                    handleChange(e)
                                                    if (isPayMethodPercentage(e.target.value)) {
                                                        setFieldValue(`${fieldName}.isPerUnit`, true)
                                                    }
                                                    else { setFieldValue(`${fieldName}.isPerUnit`, false) }
                                                }}
                                                error={hasError(index, 'payMethodId')}
                                                input={<Input id={`${fieldName}.payMethodId`} />}
                                                inputProps={{
                                                    name: `${fieldName}.payMethodId`,
                                                    id: `${fieldName}.payMethodId`,
                                                }}
                                            >
                                                {payMethods.filter((item) => item.name !== "Per Unit").map((payMethod) => (
                                                    <MenuItem
                                                        key={payMethod.id}
                                                        value={payMethod.id}
                                                    >
                                                        {payMethod.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                {getHelperText(index, "payMethodId") || ' '}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={1}>
                                        <TextField
                                            required
                                            type="number"
                                            label="Pay Value"
                                            name={`${fieldName}.payValue`}
                                            className={classes.textField}
                                            margin="normal"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={entry.payValue}
                                            helperText={getHelperText(index, "payValue") || ' '}
                                            error={hasError(index, 'payValue')}
                                        />
                                    </Grid>
                                    {shouldShowPerUnitField(values, entry) && <Grid item xs={2}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={entry.isPerUnit || false}
                                                    onChange={(e) => {
                                                        setFieldValue(`${fieldName}.isPerUnit`, e.target.checked);
                                                        if (!e.target.checked) {
                                                            setFieldValue('useBaseRate', false);
                                                        }
                                                    }}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                                    Per Unit
                                                </Typography>
                                            }
                                            sx={{ mt: 1 ,marginBottom:'-35px'}}
                                        />
                                    </Grid>}
                                    {isPayMethodAnesthesia(entry.payMethodId) && (
                                        <>
                                            <Grid item xs={3}>
                                                <Box display="flex" alignItems="center">
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: !entry.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginRight: 1,
                                                            marginLeft: 1
                                                        }}
                                                    >
                                                        Based on units
                                                    </Typography>
                                                    <Switch
                                                        checked={entry.useMinutesPerUnit || false}
                                                        onChange={(e) => {
                                                            setFieldValue(`${fieldName}.useMinutesPerUnit`, e.target.checked);

                                                            if (e.target.checked && !entry.minutesPerUnit) {
                                                                setFieldValue(`${fieldName}.minutesPerUnit`, 15);
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                   
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: entry.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginLeft: 1
                                                        }}
                                                    >
                                                        Based on minutes
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            {/* Minutes Per Unit Field - Show when useMinutesPerUnit is true */}
                                            {entry.useMinutesPerUnit && (
                                                <Grid item xs={2}>
                                                    <TextField
                                                        required
                                                        type="number"
                                                        label="Minutes Per Unit"
                                                        name={`${fieldName}.minutesPerUnit`}
                                                        className={classes.textField}
                                                        margin="normal"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={entry.minutesPerUnit}
                                                        helperText={getHelperText(index, "minutesPerUnit") || ' '}
                                                        error={hasError(index, "minutesPerUnit")}
                                                        inputProps={{ min: 1 }}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                <PopoverLink
                                                                    maxWidth={600}
                                                                    columnData={
                                                                        <i className="fas fa-question-circle text-info" style={{ cursor: 'pointer', marginLeft: 4 }}></i>
                                                                    }
                                                                    hoverData={[
                                                                        {
                                                                            label: 'Formula',
                                                                            data: '(Base Units + Time Units (Charge Units / Minutes Per Units)) x Conversion Factor (Pay Value)'
                                                                        },
                                                                        {
                                                                            label: '',
                                                                            data: '',
                                                                            type: 'SPACE'
                                                                        },
                                                                        {
                                                                            label: 'Description',
                                                                            data: 'Choose whether anesthesia values come from units or minutes on the claim. If minutes are selected, enter the conversion value (default is 15). Selecting units will not change current behavior.'
                                                                        }
                                                                    ]}
                                                                />
                                                            </InputAdornment>
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                    <RuleProviderRoleCriteriaFieldArray
                                        groupIndex={index}
                                        groupField={fieldName}
                                        values={values}
                                        handleBlur={handleBlur}
                                        handleChange={handleChange}
                                        touched={touched}
                                        errors={errors}
                                        providerRoleCriteria={providerRoleCriteria}
                                        setFieldValue={setFieldValue}
                                        disableCriteriaSelection={false}
                                        npiRoles={npiRoles}
                                    />
                                    </Grid>
                                </Grid>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        />
        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
            <DialogTitle>Confirm Fee Schedule Type Change</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Changing fee schedule type will clear all existing entries. Do you want to continue?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleConfirmTypeChange} color="primary" variant="contained">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default RuleProviderRolePaymentGroupFieldArray;
